/**
 * LocalStorage Service
 * CRUD operations for content stored in browser localStorage
 * Used for offline/demo mode when API is not available
 */

import type {
  LocalContent,
  LocalContentListResponse,
  LocalContentListParams,
  CreateLocalContentData,
  UpdateLocalContentData,
} from './types';

const STORAGE_KEY = 'ach_content';
const STORAGE_VERSION = '1.0';

/**
 * Generate a unique ID for new content
 */
function generateId(): string {
  return `local_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
}

/**
 * Get all content from localStorage
 */
function getAllContent(): LocalContent[] {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return [];
    
    const data = JSON.parse(stored);
    // Handle versioned storage
    if (data.version === STORAGE_VERSION && Array.isArray(data.items)) {
      return data.items;
    }
    // Legacy format (direct array)
    if (Array.isArray(data)) {
      return data;
    }
    return [];
  } catch {
    return [];
  }
}

/**
 * Save all content to localStorage
 */
function saveAllContent(items: LocalContent[]): void {
  const data = {
    version: STORAGE_VERSION,
    items,
    updatedAt: new Date().toISOString(),
  };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
}

/**
 * LocalStorage Service for content CRUD operations
 */
export const localStorageService = {
  /**
   * List all local content with optional filters
   */
  list(params?: LocalContentListParams): LocalContentListResponse {
    let items = getAllContent();
    
    // Filter by type
    if (params?.type) {
      items = items.filter(item => item.type === params.type);
    }
    
    // Filter by status
    if (params?.status) {
      items = items.filter(item => item.status === params.status);
    }
    
    // Filter by search (title)
    if (params?.search) {
      const searchLower = params.search.toLowerCase();
      items = items.filter(item => 
        item.title.toLowerCase().includes(searchLower)
      );
    }
    
    // Sort by updated_at descending (newest first)
    items.sort((a, b) => 
      new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
    );
    
    return {
      results: items,
      count: items.length,
    };
  },

  /**
   * Get content by ID
   */
  get(id: string): LocalContent | null {
    const items = getAllContent();
    return items.find(item => item.id === id) || null;
  },

  /**
   * Create new content
   */
  create(data: CreateLocalContentData): LocalContent {
    const items = getAllContent();
    const now = new Date().toISOString();
    
    const newContent: LocalContent = {
      id: generateId(),
      title: data.title,
      type: data.type,
      data: data.data,
      config: data.config || null,
      status: data.status || 'draft',
      created_at: now,
      updated_at: now,
    };
    
    items.push(newContent);
    saveAllContent(items);
    
    return newContent;
  },

  /**
   * Update existing content
   */
  update(id: string, data: UpdateLocalContentData): LocalContent {
    const items = getAllContent();
    const index = items.findIndex(item => item.id === id);
    
    if (index === -1) {
      throw new Error(`Content not found: ${id}`);
    }
    
    const existing = items[index];
    const updated: LocalContent = {
      ...existing,
      ...(data.title !== undefined && { title: data.title }),
      ...(data.data !== undefined && { data: data.data }),
      ...(data.config !== undefined && { config: data.config }),
      ...(data.status !== undefined && { status: data.status }),
      updated_at: new Date().toISOString(),
    };
    
    items[index] = updated;
    saveAllContent(items);
    
    return updated;
  },

  /**
   * Delete content by ID
   */
  delete(id: string): void {
    const items = getAllContent();
    const filtered = items.filter(item => item.id !== id);
    
    if (filtered.length === items.length) {
      throw new Error(`Content not found: ${id}`);
    }
    
    saveAllContent(filtered);
  },

  /**
   * Clear all local content
   */
  clear(): void {
    localStorage.removeItem(STORAGE_KEY);
  },

  /**
   * Check if there is any local content
   */
  hasContent(): boolean {
    return getAllContent().length > 0;
  },

  /**
   * Get count of local content
   */
  count(): number {
    return getAllContent().length;
  },

  /**
   * Export all content as JSON string
   */
  export(): string {
    const items = getAllContent();
    return JSON.stringify(items, null, 2);
  },

  /**
   * Import content from JSON string
   */
  import(jsonString: string, overwrite = false): number {
    try {
      const imported = JSON.parse(jsonString) as LocalContent[];
      if (!Array.isArray(imported)) {
        throw new Error('Invalid import data: expected array');
      }
      
      const existing = overwrite ? [] : getAllContent();
      const now = new Date().toISOString();
      
      // Process imported items
      const newItems = imported.map(item => ({
        ...item,
        id: generateId(), // Generate new IDs to avoid conflicts
        created_at: item.created_at || now,
        updated_at: now,
      }));
      
      saveAllContent([...existing, ...newItems]);
      return newItems.length;
    } catch (err) {
      throw new Error(`Import failed: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  },
};

// Legacy class-based API for consistency with other services
export class LocalStorageServiceClass {
  list(params?: LocalContentListParams): LocalContentListResponse {
    return localStorageService.list(params);
  }

  get(id: string): LocalContent | null {
    return localStorageService.get(id);
  }

  create(data: CreateLocalContentData): LocalContent {
    return localStorageService.create(data);
  }

  update(id: string, data: UpdateLocalContentData): LocalContent {
    return localStorageService.update(id, data);
  }

  delete(id: string): void {
    return localStorageService.delete(id);
  }

  clear(): void {
    return localStorageService.clear();
  }
}
